<template>
	<TooltipComponent
		v-if="doc.documentGenerationType == ENUMS.DocumentGenerationType.Paperwork"
		:tooltip="getProductDetails(doc)"
		direction="right"
		maxWidth="450"
	>
		<template #showFromOutside>
			<div v-if="doc.description">
				{{ doc.description }}
				<i
					v-if="doc.isLenderSpecific && $global.isAdminView"
					class="fas fa-university"
				/>
			</div>
			<div v-else>
				<span class="defaultCursor">{{ fimenu.getPaperDocumentName(doc) }}</span>
			</div>

			<div
				v-if="doc.type != ENUMS.PAPERWORK_DOCUMENT_TYPES.PaperworkDocument && doc.type != ENUMS.PAPERWORK_DOCUMENT_TYPES.AcceptDecline"
				class="defaultCursor"
			>
				Provider: {{ getPaperworkDocType(doc).provider || doc.typeString }}
			</div>

			<div
				v-if="isWetSignDoc && documentCategoryName"
				class="defaultCursor"
			>
				<em>{{ documentCategoryName }}</em>
			</div>

			<div
				v-if="$global.isAdminView"
				class="admin-details"
			>
				<div
					v-if="doc.filledDoc"
					class="details-wrapper"
				>
					<div class="latest-version">
						<strong>Config Id:</strong><span>{{ doc.paperworkDocumentConfigId }}</span>
					</div>

					<div class="latest-version">
						<strong>Latest version:</strong>

						<a
							v-if="showDocConfigLink"
							:href="docConfigPageUrl"
							target="_blank"
							>{{ extraConfigInfo.isLatest ? 'Current' : extraConfigInfo.latestId }}</a
						>
						<span v-else-if="extraConfigInfo">{{ extraConfigInfo.isLatest ? 'Current' : extraConfigInfo.latestId }}</span>
						<button
							title="More info"
							@click="() => setExtraConfigInfo(doc)"
						>
							<i class="fas fa-info"></i>
						</button>
					</div>
				</div>

				<span
					v-else
					class="details-wrapper"
				>
					<div class="latest-version">
						<strong>Config Id:</strong>
						N/A
						<button
							title="Delete error doc"
							@click="() => deleteErrorDoc(doc)"
						>
							<i class="fas fa-trash"></i>
						</button>
					</div>
				</span>
			</div>
		</template>
	</TooltipComponent>

	<div
		v-else-if="doc.documentGenerationType == ENUMS.DocumentGenerationType.Uploaded"
		class="defaultCursor"
	>
		<span class="defaultCursor">{{ doc.display }}</span>
		<br />
		<span v-if="uploadedDocumentName">{{ uploadedDocumentName }}</span>
	</div>
</template>
<script lang="ts">
	export interface IDocumentPacketDocumentNameProps {
		fimenu?: FIMenu;
		packet?: IPaperworkPacket;
		doc?: IPaperworkDocument | IPacketDocument | IUploadedDocumentWithMeta | any;
		getProductDetails?: (doc: IPacketDocument) => string[];
		getPaperworkDocType?: (doc: IPacketDocument) => { productType?: number; provider?: string; typeString?: string };
	}
</script>
<script setup lang="ts">
	import { computed, ref } from 'vue';

	import api from '@core/services/api/_api';
	import auth from '@core/services/auth';
	import settings from 'settings';
	import util from '@core/services/util';

	import { IPacketDocument, IUploadedDocumentWithMeta } from '@/components/DocumentPacket.vue';
	import { DocAssociation } from '@core/classes/SharedEnums';
	import DocumentAssociation from '@core/classes/DocumentAssociation';
	import ENUMS from '@core/classes/Enums';
	import FIMenu from '@core/classes/FIMenu';
	import { IPaperworkDocument } from '@core/classes/Paperwork/PaperworkDocument';
	import { IPaperworkPacket } from '@core/classes/Paperwork/PaperworkPacket';

	import TooltipComponent from '@core/components/TooltipComponent.vue';
	import { useRouter } from 'vue-router';

	interface IExtraConfigInfo {
		id: string;
		isLatest: boolean;
		latestId: string;
	}

	const USER = auth.getTokenPayload();

	const props = defineProps<IDocumentPacketDocumentNameProps>();
	const $router = useRouter();

	const extraConfigInfo = ref<IExtraConfigInfo>({ id: null, isLatest: false, latestId: null });

	const isWetSignDoc = computed((): boolean => {
		return !!props.doc.impactDoc || props.doc.canOnlyWetSign;
	});

	const documentCategoryName = computed((): string => {
		const category = settings.lookups.paperworkCategories.find((cat: any) => cat.id === props.doc.categoryId);
		return category.description;
	});

	const uploadedDocumentName = computed((): string => {
		// Returns the customers names associated to the document.
		const fullDoc = props.fimenu.paperwork.uploadedDocuments.find((d: any) => d.id === props.doc.id) ?? props.doc;
		const customerAssociations = fullDoc?.associations?.filter((asoc: DocumentAssociation) => asoc?.type == DocAssociation.Customer);
		if (!customerAssociations || customerAssociations.length === 0) return null;

		const customerNames = [];
		for (const association of customerAssociations) {
			const customerId = association?.id;
			if (util.isNullOrEmpty(customerId)) continue;

			if (props.fimenu.customer?.id === customerId) {
				const customerName = props.fimenu.customer?.getFullName()?.toUpperCase();
				if (customerName) customerNames.push(customerName);
			} else if (props.fimenu.coCustomer?.id === customerId) {
				const coCustomerName = props.fimenu.coCustomer?.getFullName()?.toUpperCase();
				if (coCustomerName) customerNames.push(coCustomerName);
			} else {
				const extraCustomer = props.fimenu.customersInfo.customers.find(ci => ci.id === customerId);
				if (extraCustomer?.fullName) customerNames.push(extraCustomer?.fullName?.toUpperCase());
			}
		}

		if (customerNames.length < 1) return null;

		// Not sure if a document could match both customers. But the code is very flexible
		// and allows it; therefor added support for it.
		return `Customer(s): ${customerNames.join(' & ')}`;
	});

	const showDocConfigLink = computed((): boolean => {
		return settings.environmentName !== 'PRODUCTION' && !!extraConfigInfo.value;
	});

	const docConfigPageUrl = computed((): string => {
		const docID = extraConfigInfo.value.isLatest ? props.doc.paperworkDocumentConfigId : extraConfigInfo.value.latestId;
		const docUrl = $router.resolve({
			name: 'utilities',
			query: { section: 'utilities-manage-base-paperwork', docID },
		});
		return docUrl.href;
	});

	async function setExtraConfigInfo(doc: IPaperworkDocument) {
		const response = await api.utilities.getActiveDocConfigForCode(doc.paperworkDocumentConfigId);
		const activeDocConfigId = response.data?.id;

		extraConfigInfo.value = {
			id: doc.paperworkDocumentConfigId,
			isLatest: doc.paperworkDocumentConfigId === activeDocConfigId,
			latestId: activeDocConfigId,
		};
	}

	function deleteErrorDoc(doc: IPaperworkDocument) {
		const docIndex = props.packet.documents.findIndex(d => d.id === doc.id);
		if (docIndex >= 0) {
			props.packet.documents.splice(docIndex, 1);
			util.toastr('success', 'Success', 'Document deleted');
		} else {
			util.toastr('error', 'Error', 'Error finding doc in packet');
		}
	}
</script>

<style scoped>
	.admin-details {
		margin-top: 5px;
		font-size: 12px;
	}

	.admin-details .details-wrapper {
		display: flex;
		flex-direction: column;
	}

	.admin-details .details-wrapper button {
		padding: 0;
		width: 15px;
		height: 15px;
		min-height: unset;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 8px;
	}

	.admin-details .details-wrapper .latest-version {
		display: flex;
		align-items: center;
		justify-content: flex-start;
		column-gap: 5px;
	}
</style>
